import { createApi } from '@reduxjs/toolkit/query/react';

import {
  DELETE,
  GET,
  POST,
  PUT,
  SALES_FUNNELS_STAGES_RESOURCE,
} from '@app/constants';
import { customBaseQuery } from '@app/services/rtkQuery/baseQuery';
import type { NSalesFunnelsStagesApi } from '@app/services/rtkQuery/SalesFunnelsStages/types';
import type { NAuth, NFunnels } from '@app/types';

const API_URL_PART = SALES_FUNNELS_STAGES_RESOURCE;

export const salesFunnelsStagesApi = createApi({
  reducerPath: 'salesFunnelsStagesApi',
  baseQuery: (props) => customBaseQuery(props),
  endpoints: ({ mutation, query }) => ({
    CreateSalesFunnelStage: mutation<
      NFunnels.SalesFunnelStageDto,
      NFunnels.CreateSalesFunnelStageDto
    >({
      query: ({ ActiveBranchId, ...data }) => ({
        url: `${API_URL_PART}/Create`,
        method: POST,
        data,
        params: { ActiveBranchId },
      }),
    }),
    UpdateSalesFunnelStage: mutation<
      NFunnels.SalesFunnelStageDto,
      {
        id: number;
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
        data: NFunnels.UpdateSalesFunnelStageDto;
      }
    >({
      query: ({ id, data, ActiveBranchId }) => ({
        url: `${API_URL_PART}/Update/${id}`,
        method: PUT,
        params: { ActiveBranchId },
        data,
      }),
    }),
    GetSalesFunnelStages: query<
      NFunnels.SalesFunnelStageListDto[],
      {
        salesFunnelId: string | null;
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
      }
    >({
      query: ({ salesFunnelId, ...params }) => ({
        url: `${API_URL_PART}/List/${salesFunnelId}`,
        method: GET,
        params,
      }),
    }),
    GetSalesFunnelStagesWithRobots: query<
      NFunnels.SalesFunnelStageListDto[],
      {
        salesFunnelId: string | null;
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
      }
    >({
      query: ({ salesFunnelId, ...params }) => ({
        url: `${API_URL_PART}/List/${salesFunnelId}/Robots`,
        method: GET,
        params,
      }),
    }),
    DeleteSalesFunnelStage: mutation<
      void,
      {
        id: number;
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
      }
    >({
      query: ({ id, ...params }) => ({
        url: `${API_URL_PART}/Delete/${id}`,
        method: DELETE,
        params,
      }),
    }),
    GetRobotsListTypes: query<
      Record<string, string>,
      {
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
      }
    >({
      query: ({ ...params }) => ({
        url: `${API_URL_PART}/Robots/ListTypes`,
        method: GET,
        params,
      }),
    }),
    GetRobotsListTriggers: query<
      Record<string, string>,
      {
        robotType: number;
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
      }
    >({
      query: ({ ...params }) => ({
        url: `${API_URL_PART}/Robots/ListTriggers`,
        method: GET,
        params,
      }),
    }),
    GetSalesFunnelStagesListForId: query<
      NFunnels.SalesFunnelStageFilterItemDto[],
      {
        salesFunnelId: number;
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
      }
    >({
      query: ({ salesFunnelId, ...params }) => ({
        url: `${API_URL_PART}/List/${salesFunnelId}`,
        method: GET,
        params,
      }),
    }),
    CreateRobot: mutation<
      void,
      {
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
        data: NSalesFunnelsStagesApi.UpdateRobotRequest;
      }
    >({
      query: ({ data, ...params }) => ({
        url: `${API_URL_PART}/Robots/Create`,
        method: POST,
        params,
        data,
      }),
    }),
    UpdateRobot: mutation<
      void,
      {
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
        id: number;
        data: NSalesFunnelsStagesApi.UpdateRobotRequest;
      }
    >({
      query: ({ id, data, ...params }) => ({
        url: `${API_URL_PART}/Robots/Update/${id}`,
        method: PUT,
        params,
        data,
      }),
    }),
    DeleteRobot: mutation<
      void,
      {
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
        id: number;
      }
    >({
      query: ({ id, ...params }) => ({
        url: `${API_URL_PART}/Robots/Delete/${id}`,
        method: DELETE,
        params,
      }),
    }),
    GetRobotForId: query<
      NSalesFunnelsStagesApi.GetRobotForIdResponse,
      {
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
        id: number;
      }
    >({
      query: ({ id, ...params }) => ({
        url: `${API_URL_PART}/Robots/Get/${id}`,
        method: GET,
        params,
      }),
    }),
    GetRobotsListForStageId: query<
      NSalesFunnelsStagesApi.UpdateRobotRequest[],
      {
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
        stageId: number;
      }
    >({
      query: ({ stageId, ...params }) => ({
        url: `${API_URL_PART}/Robots/List/${stageId}`,
        method: GET,
        params,
      }),
    }),
    SearchLogs: mutation<
      NSalesFunnelsStagesApi.ModelRobotLogsSearchResult,
      NSalesFunnelsStagesApi.SearchLogsRequest
    >({
      query: ({ data, ...params }) => ({
        url: `${API_URL_PART}/Robots/SearchLogs`,
        method: POST,
        params,
        data,
      }),
    }),
    GetLogsSearchResult: query<
      NSalesFunnelsStagesApi.ModelRobotLogsSearchResult,
      NSalesFunnelsStagesApi.GetLogsSearchResultRequest
    >({
      query: (params) => ({
        url: `${API_URL_PART}/Robots/GetLogsSearchResult`,
        method: GET,
        params,
      }),
    }),
    LogsMarkAsRead: mutation<
      void,
      NSalesFunnelsStagesApi.LogsMarkAsReadRequest
    >({
      query: ({ data, ...params }) => ({
        url: `${API_URL_PART}/Robots/LogsMarkAsRead`,
        method: POST,
        params,
        data,
      }),
    }),
  }),
});

export const {
  useCreateSalesFunnelStageMutation,
  useUpdateSalesFunnelStageMutation,
  useGetSalesFunnelStagesQuery,
  useDeleteSalesFunnelStageMutation,
  useGetSalesFunnelStagesWithRobotsQuery,
  useGetRobotsListTriggersQuery,
  useGetRobotsListTypesQuery,
  useLazyGetRobotsListTriggersQuery,
  useLazyGetRobotsListTypesQuery,
  useGetSalesFunnelStagesListForIdQuery,
  useLazyGetSalesFunnelStagesListForIdQuery,
  useCreateRobotMutation,
  useDeleteRobotMutation,
  useUpdateRobotMutation,
  useGetRobotForIdQuery,
  useLazyGetRobotForIdQuery,
  useGetRobotsListForStageIdQuery,
  useLazyGetRobotsListForStageIdQuery,
  useSearchLogsMutation,
  useLazyGetLogsSearchResultQuery,
  useLogsMarkAsReadMutation,
} = salesFunnelsStagesApi;
